import { SITE_URL } from './site-url'

interface NewsPost {
  _id: string
  title: string
  slug: {
    current: string
  }
  excerpt: string
  publishedAt: string
  author?: {
    name: string
    bio?: string
  }
  body?: unknown
  mainImage?: {
    asset: {
      url: string
    }
    alt?: string
  }
  categories?: Array<{
    title: string
  }>
  seo?: {
    metaTitle?: string
    metaDescription?: string
    keywords?: string
  }
  tags?: string[]
  readTime?: number
}

interface TeamMember {
  name: string
  role: string
  image?: string
  bio?: string
  social?: {
    linkedin?: string
    twitter?: string
    github?: string
  }
}

interface WebsiteStructuredData {
  '@context': 'https://schema.org'
  '@type': 'Organization'
  name: string
  url: string
  logo: string
  description: string
  foundingDate: string
  contactPoint: {
    '@type': 'ContactPoint'
    telephone: string
    contactType: 'customer service'
    areaServed: string
    availableLanguage: string[]
  }
  address: {
    '@type': 'PostalAddress'
    addressLocality: string
    addressCountry: string
  }
  sameAs: string[]
  potentialAction: {
    '@type': 'SearchAction'
    target: string
    'query-input': string
  }
}

interface NewsPostStructuredData {
  '@context': 'https://schema.org'
  '@type': 'NewsArticle'
  headline: string
  description: string
  image: string[]
  datePublished: string
  dateModified: string
  author: {
    '@type': 'Person'
    name: string
    image?: string
  }
  publisher: {
    '@type': 'Organization'
    name: string
    logo: {
      '@type': 'ImageObject'
      url: string
    }
  }
  mainEntityOfPage: {
    '@type': 'WebPage'
    '@id': string
  }
  articleSection: string
  wordCount?: number
  readingTime?: string
}

interface PersonStructuredData {
  '@context': 'https://schema.org'
  '@type': 'Person'
  name: string
  jobTitle: string
  worksFor: {
    '@type': 'Organization'
    name: string
  }
  image?: string
  description?: string
  sameAs?: string[]
}

interface ServiceStructuredData {
  '@context': 'https://schema.org'
  '@type': 'Service'
  name: string
  description: string
  provider: {
    '@type': 'Organization'
    name: string
  }
  areaServed: string
  serviceType: string
  category: string
}

interface BreadcrumbStructuredData {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item: string
  }>
}

interface FAQStructuredData {
  '@context': 'https://schema.org'
  '@type': 'FAQPage'
  mainEntity: Array<{
    '@type': 'Question'
    name: string
    acceptedAnswer: {
      '@type': 'Answer'
      text: string
    }
  }>
}

interface LocalBusinessStructuredData {
  '@context': 'https://schema.org'
  '@type': 'LocalBusiness'
  '@id': string
  name: string
  image: string
  url: string
  telephone: string
  priceRange: string
  address: {
    '@type': 'PostalAddress'
    streetAddress: string
    addressLocality: string
    addressRegion: string
    postalCode: string
    addressCountry: string
  }
  geo: {
    '@type': 'GeoCoordinates'
    latitude: number
    longitude: number
  }
  openingHoursSpecification: Array<{
    '@type': 'OpeningHoursSpecification'
    dayOfWeek: string[]
    opens: string
    closes: string
  }>
  sameAs: string[]
}

interface DubaiLocalBusinessStructuredData {
  '@context': 'https://schema.org'
  '@type': 'ProfessionalService'
  '@id': string
  name: string
  description: string
  image: string
  url: string
  telephone: string
  email: string
  priceRange: string
  address: {
    '@type': 'PostalAddress'
    addressLocality: string
    addressRegion: string
    addressCountry: string
  }
  geo: {
    '@type': 'GeoCoordinates'
    latitude: number
    longitude: number
  }
  areaServed: Array<{
    '@type': 'Place'
    name: string
  }>
  serviceType: string[]
  openingHoursSpecification: Array<{
    '@type': 'OpeningHoursSpecification'
    dayOfWeek: string[]
    opens: string
    closes: string
  }>
  sameAs: string[]
  parentOrganization: {
    '@type': 'Organization'
    name: string
    url: string
  }
}

const SITE_NAME = 'Demirix'
/*
 * Points at a file that is actually served.
 *
 * This was `${SITE_URL}/assets/dark-logo.webp`, which 404s. The logo lives in src/assets,
 * so Vite hashes it into dist/assets/dark-logo-<hash>.webp and the unhashed path never
 * exists; there is no public/assets directory either. The Amplify rewrite excludes webp
 * from its fallback to index.html, so the request 404s rather than returning HTML. It was
 * reaching six schemas, including publisher.logo on every article.
 *
 * The press kit logo is the right target: it lives under public, so its URL is stable
 * rather than content hashed, and it is already deployed and verified as image/png.
 */
const ORGANIZATION_LOGO = `${SITE_URL}/press/demirix-logo-full-black.png`

export const generateOrganizationSchema = (): WebsiteStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: ORGANIZATION_LOGO,
  description: 'Leading IT consultancy providing web development, cloud solutions, and digital transformation services. Helping businesses leverage technology for growth and innovation.',
  foundingDate: '2023-03-07',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+971 58 551 8885',
    contactType: 'customer service',
    areaServed: 'Worldwide',
    availableLanguage: ['English', 'Arabic']
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Dubai',
    addressCountry: 'AE'
  },
  sameAs: [
    'https://linkedin.com/company/demirix',
    'https://x.com/Demirix_com',
    'https://www.facebook.com/share/gP6uwno2b9Z5aYPH/',
    'https://www.instagram.com/demirix_com',
    'https://maps.app.goo.gl/CEHTH58FodDTk9jW9'
  ],
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/news?search={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
})

export const generateWebsiteStructuredData = generateOrganizationSchema;

export const generateNewsPostStructuredData = (post: NewsPost, fullUrl: string): NewsPostStructuredData => {
  const images = []
  // Add main image if available
  if (post.mainImage?.asset?.url) {
    images.push(post.mainImage.asset.url)
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    description: post.excerpt || post.title,
    image: images,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt, // Using publishedAt as we don't have _updatedAt
    author: {
      '@type': 'Person',
      name: post.author?.name || 'Demirix Team',
      image: undefined // No image field in simplified author
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_LOGO
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    articleSection: post.categories?.[0]?.title || 'Technology',
    wordCount: post.body ? estimateWordCount(Array.isArray(post.body) ? post.body : []) : undefined,
    readingTime: post.readTime ? `${post.readTime} min read` : undefined
  }
}

export const generatePersonStructuredData = (person: TeamMember): PersonStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: person.name,
  jobTitle: person.role,
  worksFor: {
    '@type': 'Organization',
    name: SITE_NAME
  },
  image: person.image || undefined,
  description: person.bio || undefined,
  sameAs: person.social ? Object.values(person.social).filter(Boolean) as string[] : undefined
})

export const generateServiceSchema = (service: { name: string; description: string; provider?: string; areaServed?: string; offers?: { price: string; priceCurrency: string } }): ServiceStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: service.name,
  description: service.description,
  provider: {
    '@type': 'Organization',
    name: service.provider || SITE_NAME
  },
  areaServed: service.areaServed || 'Worldwide',
  serviceType: 'IT Consulting',
  category: 'Technology Services'
})

export const generateServiceStructuredData = generateServiceSchema;

export const generateBreadcrumbStructuredData = (breadcrumbs: Array<{ name: string; path: string }>): BreadcrumbStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: `${SITE_URL}${crumb.path}`
  }))
})

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>): FAQStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
})

export const generateLocalBusinessSchema = (): LocalBusinessStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': SITE_URL,
  name: 'Demirix Technology LLC',
  image: ORGANIZATION_LOGO,
  url: SITE_URL,
  telephone: '+971 58 551 8885',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Tamani Arts Building, 1823 Al Asayel St, Business Bay',
    addressLocality: 'Dubai',
    addressRegion: 'Dubai',
    postalCode: '121612',
    addressCountry: 'AE'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 25.1857,
    longitude: 55.2644
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Sunday'],
      opens: '09:00',
      closes: '18:00'
    }
  ],
  sameAs: [
    'https://linkedin.com/company/demirix',
    'https://x.com/Demirix_com',
    'https://www.facebook.com/share/gP6uwno2b9Z5aYPH/',
    'https://www.instagram.com/demirix_com',
    'https://maps.app.goo.gl/CEHTH58FodDTk9jW9'
  ]
})

interface ArticleStructuredData {
  '@context': 'https://schema.org'
  '@type': 'Article'
  headline: string
  description: string
  image: string[]
  datePublished: string
  dateModified: string
  author: {
    '@type': 'Organization'
    name: string
    url: string
  }
  publisher: {
    '@type': 'Organization'
    name: string
    logo: {
      '@type': 'ImageObject'
      url: string
    }
  }
  mainEntityOfPage: {
    '@type': 'WebPage'
    '@id': string
  }
  articleSection: string
  keywords: string[]
  wordCount?: number
}

export const generateArticleSchema = (article: {
  headline: string
  description: string
  image?: string
  datePublished: string
  dateModified?: string
  articleSection?: string
  keywords?: string[]
  wordCount?: number
  url: string
}): ArticleStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.headline,
  description: article.description,
  /*
   * Deliberately NOT og-card.png, which is the bare DD mark.
   *
   * Google's Article guidance asks for an image that represents the article and names a
   * logo as the wrong choice. og-image-default.png is the retired preview card: still not
   * article content, and still branding rather than a photograph, but it carries the name
   * and the tagline, so it is the less wrong of the two files available today.
   *
   * Be clear that this does not satisfy the guidance, it only stops making it worse. The
   * real answer is a per post image, which is its own piece of work and is not in DMX-047.
   * Two related facts, both checked rather than assumed: the recommended aspect ratios are
   * 16x9, 4x3 and 1x1, and 1200 by 630 is none of them; and large Discover thumbnails need
   * max-image-preview:large, which this site does not send, so any Discover argument for
   * one image over another does not currently apply here at all.
   */
  image: article.image ? [article.image] : [`${SITE_URL}/og-image-default.png`],
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: ORGANIZATION_LOGO
    }
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': article.url
  },
  articleSection: article.articleSection || 'Technology',
  keywords: article.keywords || [],
  wordCount: article.wordCount
})

export const generateDubaiLocalBusinessSchema = (): DubaiLocalBusinessStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/services/dubai`,
  name: 'Demirix Technology LLC - Software Development Dubai',
  description: 'Professional software and web development services in Dubai. We deliver custom software solutions, web applications, mobile app development, and IT consulting services for businesses in Dubai and the Middle East.',
  image: ORGANIZATION_LOGO,
  url: `${SITE_URL}/services`,
  telephone: '+971 58 551 8885',
  email: 'hello@demirix.com',
  priceRange: '$$$$',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Dubai',
    addressRegion: 'Dubai',
    addressCountry: 'AE'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 25.194154,
    longitude: 55.2894694
  },
  areaServed: [
    {
      '@type': 'Place',
      name: 'Dubai'
    },
    {
      '@type': 'Place',
      name: 'United Arab Emirates'
    },
    {
      '@type': 'Place',
      name: 'Middle East'
    }
  ],
  serviceType: [
    'Software Development',
    'Web Development',
    'Mobile App Development',
    'IT Consulting',
    'Cloud Solutions',
    'AI Integration',
    'Blockchain Development'
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
      opens: '09:00',
      closes: '18:00'
    }
  ],
  sameAs: [
    'https://linkedin.com/company/demirix',
    'https://x.com/Demirix_com',
    'https://www.facebook.com/share/gP6uwno2b9Z5aYPH/',
    'https://www.instagram.com/demirix_com',
    'https://maps.app.goo.gl/CEHTH58FodDTk9jW9'
  ],
  parentOrganization: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL
  }
})

// Helper function to estimate word count from portable text content
const estimateWordCount = (content: unknown[]): number => {
  if (!content || !Array.isArray(content)) return 0

  let wordCount = 0

  const countWords = (block: { _type?: string; children?: Array<{ text?: string }> }) => {
    if (block._type === 'block' && block.children) {
      for (const child of block.children) {
        if (child.text && typeof child.text === 'string') {
          wordCount += child.text.trim().split(/\s+/).length
        }
      }
    }
  }

  content.forEach((block: unknown) => {
    if (typeof block === 'object' && block !== null) {
      countWords(block as { _type?: string; children?: Array<{ text?: string }> })
    }
  })
  return wordCount
}